import { useEffect, useMemo, useRef, useState } from 'react';
import type { MinigameViewProps } from '../../types/minigame';

// Port Scan Sweep (level 10) — reflex. Ports light up across the grid; tag the OPEN ones
// (+1), avoid the HONEYPOTS (-2), ignore the closed ones (harmless). Reach the quota before
// the clock runs out.
//
// The wave schedule comes from the server (it has to, to be drawable) and the server keeps
// the authoritative copy: this component only records WHEN the player tapped WHICH cell, and
// the server replays the schedule to decide what each tap was worth. The counters below are
// therefore a local preview — the result screen shows the server's verdict.

interface Wave { id: number; cell: number; kind: 'open' | 'trap' | 'closed'; startMs: number; endMs: number }

interface Challenge {
  cols: number;
  rows: number;
  quota: number;
  windowMs: number;
  timeLimitMs: number;
  /** High Port Scanner: honeypots flash a warning before they arm. */
  telegraph: boolean;
  waves: Wave[];
}

interface Tap { cell: number; tMs: number }

/** How long before a honeypot arms that the warning shows (only when telegraph is on). */
const TELEGRAPH_MS = 350;
const FRAME_MS = 50;

export default function PortScanSweepView({ challenge, onSubmit, setPendingSubmission, startedAgoMs, busy }: MinigameViewProps<Challenge>) {
  const cols = challenge?.cols ?? 6;
  const rows = challenge?.rows ?? 4;
  const quota = challenge?.quota ?? 12;
  const waves = useMemo(() => challenge?.waves ?? [], [challenge]);

  // The clock is anchored to THIS challenge during render, not set inside an effect. That
  // matters: an effect that re-fires (e.g. because a parent passes an unstable callback, and
  // this page re-renders every second while the countdown runs) would keep resetting the
  // start time, freezing the schedule near t=0 and leaving the grid apparently dead.
  // The anchor is pulled BACK by however long the attempt has already been running, so a
  // resumed attempt lines up with the server's clock instead of replaying its schedule from
  // zero — otherwise every input would land outside the window the server is grading against.
  const anchorRef = useRef<{ key: unknown; at: number }>({ key: challenge, at: Date.now() - startedAgoMs });
  if (anchorRef.current.key !== challenge) anchorRef.current = { key: challenge, at: Date.now() - startedAgoMs };
  const startedAt = anchorRef.current.at;

  const tapsRef = useRef<Tap[]>([]);
  const countedRef = useRef<Set<number>>(new Set());

  const [elapsed, setElapsed] = useState(0);
  const [hits, setHits] = useState(0);
  const [traps, setTraps] = useState(0);
  const [flash, setFlash] = useState<{ cell: number; kind: string } | null>(null);

  // Fresh attempt → clear the recorded taps and counters.
  useEffect(() => {
    tapsRef.current = [];
    countedRef.current = new Set();
    setHits(0);
    setTraps(0);
    setFlash(null);
    setPendingSubmission({ taps: [] });
    // setPendingSubmission is deliberately NOT a dependency: if a caller ever passes an
    // unstable function, re-running this would wipe the player's progress mid-attempt.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [challenge]);

  // Local clock driving the animation; self-corrects from the anchor above.
  useEffect(() => {
    const id = setInterval(() => setElapsed(Date.now() - anchorRef.current.at), FRAME_MS);
    return () => clearInterval(id);
  }, []);

  const points = hits - 2 * traps;

  // Quota reached → submit straight away. The server re-verifies, and its tolerance is more
  // generous than this preview, so a local quota is safe to act on.
  useEffect(() => {
    if (!busy && points >= quota) onSubmit({ taps: tapsRef.current });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [points, quota, busy]);

  // Currently lit appearances, and honeypots about to arm.
  const active = new Map<number, Wave>();
  const warning = new Set<number>();
  for (const w of waves) {
    if (elapsed >= w.startMs && elapsed <= w.endMs) active.set(w.cell, w);
    else if (challenge?.telegraph && w.kind === 'trap'
             && elapsed >= w.startMs - TELEGRAPH_MS && elapsed < w.startMs) warning.add(w.cell);
  }

  const tap = (cell: number) => {
    if (busy) return;
    const tMs = Date.now() - startedAt;
    const w = active.get(cell);

    // Always record the tap — the server decides what it was worth.
    tapsRef.current = [...tapsRef.current, { cell, tMs }];
    setPendingSubmission({ taps: tapsRef.current });

    // Local preview only, deduped per appearance exactly like the server does.
    if (w && !countedRef.current.has(w.id)) {
      countedRef.current.add(w.id);
      if (w.kind === 'open') setHits(h => h + 1);
      else if (w.kind === 'trap') setTraps(t => t + 1);
    }
    setFlash({ cell, kind: w?.kind ?? 'miss' });
    setTimeout(() => setFlash(f => (f?.cell === cell ? null : f)), 160);
  };

  const cellStyle = (i: number): React.CSSProperties => {
    const w = active.get(i);
    const isWarning = warning.has(i);
    let bg = '#0f1218', border = '#22262e', color = '#3a4250', glow = 'none';

    if (w?.kind === 'open')   { bg = '#052e16'; border = '#4ade80'; color = '#86efac'; glow = '0 0 10px rgba(74,222,128,0.45)'; }
    if (w?.kind === 'trap')   { bg = '#2d0a0a'; border = '#f87171'; color = '#fca5a5'; glow = '0 0 10px rgba(248,113,113,0.45)'; }
    if (w?.kind === 'closed') { bg = '#1a1d24'; border = '#4b5563'; color = '#9ca3af'; }
    if (isWarning)            { border = '#fbbf24'; color = '#fde047'; glow = '0 0 8px rgba(251,191,36,0.35)'; }

    if (flash?.cell === i) {
      border = flash.kind === 'open' ? '#4ade80' : flash.kind === 'trap' ? '#f87171' : '#6b7280';
      glow = `0 0 14px ${flash.kind === 'open' ? 'rgba(74,222,128,0.7)' : flash.kind === 'trap' ? 'rgba(248,113,113,0.7)' : 'rgba(107,114,128,0.5)'}`;
    }

    return {
      aspectRatio: '1 / 1', minWidth: 0,
      background: bg, border: `1px solid ${border}`, color,
      borderRadius: 4, cursor: busy ? 'not-allowed' : 'pointer',
      fontFamily: 'monospace', fontSize: '0.62rem', fontWeight: 'bold',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      boxShadow: glow, transition: 'box-shadow 0.1s, border-color 0.1s',
      userSelect: 'none', padding: 0,
    };
  };

  // Port numbers are cosmetic, but they sell the fiction.
  const portLabel = (i: number) => 1000 + i * 7;

  // A schedule that arrived empty would render a grid that never lights up. Say so.
  if (waves.length === 0) {
    return (
      <div style={{ fontFamily: 'monospace', color: '#f87171', fontSize: '0.85rem' }}>
        This sweep arrived without a wave schedule — nothing to scan. Reload and try again.
      </div>
    );
  }

  return (
    <div style={{ fontFamily: 'monospace' }}>
      <p style={{ color: '#cbd5e1', fontSize: '0.82rem', margin: '0 0 0.6rem', lineHeight: 1.5 }}>
        Tag <span style={{ color: '#86efac' }}>open ports</span> (+1), avoid{' '}
        <span style={{ color: '#fca5a5' }}>honeypots</span> (−2).{' '}
        <span style={{ color: '#9ca3af' }}>Closed</span> ports are harmless. Reach{' '}
        <strong>{quota} points</strong> before the clock runs out.
        {challenge?.telegraph && (
          <> Your Port Scanner <span style={{ color: '#fde047' }}>pre-flags honeypots</span>.</>
        )}
      </p>

      <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'center', marginBottom: '0.6rem' }}>
        <span style={{ fontSize: '0.85rem' }}>
          <span style={{ color: 'var(--text-muted)' }}>Points </span>
          <strong style={{ color: points >= quota ? '#4ade80' : '#e2e8f0' }}>{points}</strong>
          <span style={{ color: 'var(--text-muted)' }}> / {quota}</span>
        </span>
        <span style={{ fontSize: '0.78rem', color: '#86efac' }}>{hits} tagged</span>
        {traps > 0 && <span style={{ fontSize: '0.78rem', color: '#fca5a5' }}>{traps} honeypot{traps === 1 ? '' : 's'}</span>}
      </div>

      {/* Progress toward the quota */}
      <div style={{ height: 4, background: '#1a1a2e', borderRadius: 2, overflow: 'hidden', marginBottom: '0.7rem' }}>
        <div style={{
          height: '100%', width: `${Math.max(0, Math.min(100, (points / quota) * 100))}%`,
          background: points >= quota ? '#4ade80' : 'var(--primary-color)', transition: 'width 0.15s',
        }} />
      </div>

      <div style={{
        display: 'grid', gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
        gap: '0.35rem', maxWidth: `${cols * 3.2}rem`, marginBottom: '0.8rem',
      }}>
        {Array.from({ length: cols * rows }, (_, i) => (
          <button key={i} onClick={() => tap(i)} disabled={busy} style={cellStyle(i)}>
            {active.has(i) ? portLabel(i) : ''}
          </button>
        ))}
      </div>

      <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'center', flexWrap: 'wrap' }}>
        <button
          className="primary"
          onClick={() => onSubmit({ taps: tapsRef.current })}
          disabled={busy}
          style={{ fontSize: '0.82rem', padding: '0.35rem 0.9rem' }}
        >
          {busy ? '…' : 'End sweep'}
        </button>
        <span style={{ color: '#4b5563', fontSize: '0.72rem' }}>
          Ends automatically when you reach the quota, or when the clock runs out.
        </span>
      </div>
    </div>
  );
}
